import { useEffect, useRef, useState, useCallback } from 'react';
import printFacade from '../services/printFacade';
import { filterPhysicalPrinters } from '../utils/printerAvailability';
import { PRINT_ROLE, subscribePrinterRolesChanged } from '../domain/print';

const POLL_MS = 30_000;

const emptyStatus = {
  connected: false,
  printerName: null,
  checking: false,
  roles: [],
  receiptStatus: 'unassigned',
  allReady: false,
  partial: false,
  unconfiguredPhysical: false,
  summaryText: null
};

/**
 * Role-aware printer connectivity for layout POS badge.
 * Green only when receipt role target is present; amber when physical exists but unassigned.
 * Refreshes on assignment events + interval poll.
 */
export function usePrinterConnectedStatus(enabled = true) {
  const [status, setStatus] = useState({
    ...emptyStatus,
    checking: Boolean(enabled)
  });
  const mountedRef = useRef(true);
  const checkRef = useRef(null);

  const runCheck = useCallback(async () => {
    if (typeof window === 'undefined') {
      if (mountedRef.current) {
        setStatus({ ...emptyStatus, checking: false });
      }
      return;
    }

    try {
      if (!window.electronAPI?.listPrinters) {
        if (mountedRef.current) {
          setStatus({ ...emptyStatus, checking: false });
        }
        return;
      }

      const list = await printFacade.listPrinters();
      const all = Array.isArray(list) ? list : list?.printers || [];
      const physical = filterPhysicalPrinters(all);
      const summary = printFacade.summarizePrinterRolesHealth(physical);

      let connected = Boolean(summary.connected);
      let printerName = summary.printerName;
      let unconfiguredPhysical = Boolean(summary.unconfiguredPhysical);

      try {
        const probe = await printFacade.probePhysicalPrinter({
          role: PRINT_ROLE.RECEIPT,
          force: true
        });
        if (summary.hasConfiguredTarget) {
          connected = Boolean(probe?.connected);
          if (connected) {
            printerName = probe.printerName || probe.deviceName || printerName;
          }
        } else if (probe?.connected && physical.length > 0) {
          unconfiguredPhysical = true;
          printerName = probe.printerName || probe.deviceName || printerName;
        }
      } catch {
        /* keep summary */
      }

      const showBadge = connected || unconfiguredPhysical;

      if (mountedRef.current) {
        setStatus({
          connected: showBadge,
          printerName,
          checking: false,
          roles: summary.roles || [],
          receiptStatus: summary.receiptStatus,
          allReady: summary.allReady,
          partial: Boolean(summary.partial) || unconfiguredPhysical,
          unconfiguredPhysical,
          summaryText: buildRoleSummaryLine(summary.roles || [])
        });
      }
    } catch {
      if (mountedRef.current) {
        setStatus({ ...emptyStatus, checking: false });
      }
    }
  }, []);

  checkRef.current = runCheck;

  useEffect(() => {
    mountedRef.current = true;

    if (!enabled) {
      setStatus({ ...emptyStatus, checking: false });
      return undefined;
    }

    void runCheck();
    const intervalId = setInterval(() => {
      void runCheck();
    }, POLL_MS);

    const unsub = subscribePrinterRolesChanged(() => {
      printFacade.invalidateResolvedPrinterCache?.();
      void runCheck();
    });

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
      unsub();
    };
  }, [enabled, runCheck]);

  return status;
}

function buildRoleSummaryLine(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return null;
  return roles
    .map((r) => {
      const short =
        r.role === 'receipt' ? 'POS' : r.role === 'document' ? 'Doc' : r.role === 'label' ? 'Lbl' : r.role;
      const name = r.effectiveDisplayName || r.displayName || '—';
      const st =
        r.status === 'ok' || r.status === 'fallback_ok'
          ? 'ok'
          : r.status === 'unassigned'
            ? 'auto'
            : r.status;
      return `${short}:${st}${name && name !== '—' ? `(${name})` : ''}`;
    })
    .join(' · ');
}

export { filterPhysicalPrinters, isVirtualPrinterEntry, pickPreferredPhysicalPrinter } from '../utils/printerAvailability';
